let ctx: AudioContext | null = null;

function getCtx(): AudioContext {
  if (!ctx) ctx = new AudioContext();
  return ctx;
}

// iOS Safari requires AudioContext to be resumed AND a buffer source started
// within a direct user gesture. Call this on first pointerdown/touchstart.
export function unlockAudio(): void {
  const ac = getCtx();
  ac.resume().then(() => {
    const buf = ac.createBuffer(1, 1, ac.sampleRate);
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.connect(ac.destination);
    src.start(0);
  });
}

// ── Lobby music ───────────────────────────────────────────────────────────────

let lobbyAudio: HTMLAudioElement | null = null;
let muted: boolean = localStorage.getItem('muted') === 'true';
let pendingPlay = false;

function getLobbyAudio(): HTMLAudioElement {
  if (!lobbyAudio) {
    lobbyAudio = new Audio('/knightly-lobby-wait.mp3');
    lobbyAudio.loop = true;
    lobbyAudio.volume = 0.35;
  }
  return lobbyAudio;
}

function notifyMusicStarted(): void {
  document.dispatchEvent(new Event('lobby-music-started'));
}

function onGestureRetry(e: Event): void {
  if (!pendingPlay || muted) return;
  if ((e.target as Element)?.closest('button')) return;
  getLobbyAudio().play().then(() => {
    pendingPlay = false;
    document.removeEventListener('pointerdown', onGestureRetry as EventListener, true);
    document.removeEventListener('touchstart', onGestureRetry as EventListener, true);
    notifyMusicStarted();
  }).catch(() => {});
}

export function playLobbyMusic(): void {
  if (muted) return;
  getLobbyAudio().play().then(() => {
    notifyMusicStarted();
  }).catch(() => {
    // Autoplay blocked — retry on the next user gesture
    pendingPlay = true;
    document.addEventListener('pointerdown', onGestureRetry as EventListener, { capture: true });
    document.addEventListener('touchstart', onGestureRetry as EventListener, { capture: true });
  });
}

export function isLobbyPlaying(): boolean {
  return !!lobbyAudio && !lobbyAudio.paused;
}

export function stopLobbyMusic(): void {
  pendingPlay = false;
  document.removeEventListener('pointerdown', onGestureRetry as EventListener, true);
  document.removeEventListener('touchstart', onGestureRetry as EventListener, true);
  getLobbyAudio().pause();
}

export function toggleMute(): boolean {
  muted = !muted;
  localStorage.setItem('muted', String(muted));
  if (muted) {
    stopLobbyMusic();
  } else {
    playLobbyMusic();
  }
  return muted;
}

export function isMuted(): boolean {
  return muted;
}

// Short wooden thud — like a chess piece placed on a board
function woodThud(time: number, vol = 0.6, pitch = 180): void {
  const ac = getCtx();

  // Low thud
  const osc = ac.createOscillator();
  const oscGain = ac.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(pitch, time);
  osc.frequency.exponentialRampToValueAtTime(pitch * 0.4, time + 0.06);
  oscGain.gain.setValueAtTime(vol, time);
  oscGain.gain.exponentialRampToValueAtTime(0.001, time + 0.12);
  osc.connect(oscGain);
  oscGain.connect(ac.destination);
  osc.start(time);
  osc.stop(time + 0.15);

  // Woody click (filtered noise)
  const bufLen = Math.floor(ac.sampleRate * 0.04);
  const buf = ac.createBuffer(1, bufLen, ac.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < bufLen; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / bufLen);
  const src = ac.createBufferSource();
  src.buffer = buf;
  const lpf = ac.createBiquadFilter();
  lpf.type = 'lowpass';
  lpf.frequency.value = 1200;
  const nGain = ac.createGain();
  nGain.gain.setValueAtTime(vol * 0.4, time);
  src.connect(lpf);
  lpf.connect(nGain);
  nGain.connect(ac.destination);
  src.start(time);
}

// Regular move
export function playMove(): void {
  woodThud(getCtx().currentTime, 0.5, 200);
}

// Capture — heavier thud
export function playCapture(): void {
  const ac = getCtx();
  woodThud(ac.currentTime, 0.7, 140);
  woodThud(ac.currentTime + 0.04, 0.4, 110);
}

// Check — clean high ding
export function playCheck(): void {
  const ac = getCtx();
  [880, 1100].forEach((f, i) => {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(f, ac.currentTime + i * 0.07);
    gain.gain.setValueAtTime(0.3, ac.currentTime + i * 0.07);
    gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + i * 0.07 + 0.5);
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.start(ac.currentTime + i * 0.07);
    osc.stop(ac.currentTime + i * 0.07 + 0.5);
  });
}

// Game over — two descending notes like chess.com
export function playGameOver(): void {
  const ac = getCtx();
  [523, 392].forEach((f, i) => {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(f, ac.currentTime + i * 0.3);
    gain.gain.setValueAtTime(0.4, ac.currentTime + i * 0.3);
    gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + i * 0.3 + 0.6);
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.start(ac.currentTime + i * 0.3);
    osc.stop(ac.currentTime + i * 0.3 + 0.7);
  });
}
